import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Container,
  IconButton,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  Fade,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import {
  ArrowForward as ArrowForwardIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  DirectionsCar as CarsIcon,
  People as PeopleIcon,
  AccessTime as ScheduleIcon,
  Route as RouteIcon,
  Analytics as AnalyticsIcon,
  Create as TemplateIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Devices as DevicesIcon,
  Smartphone as SmartphoneIcon,
  Gavel as GavelIcon,
  PrivacyTip as PrivacyTipIcon,
  LocalHospital as MedicalIcon,
  DocumentScanner as ScannerIcon,
  AssignmentTurnedIn as ApprovalIcon,
  ReceiptLong as BillingIcon,
  SupportAgent as AgentIcon,
  Draw as SignatureIcon,
  EventRepeat as RecurringIcon,
  EuroSymbol as EuroIcon,
  VerifiedUser as ShieldIcon,
  CheckCircleOutline as CheckIcon,
  PhoneInTalk as PhoneInTalkIcon,
  Bolt as BoltIcon,
  RequestQuote as ReceiptIcon,
  Loyalty as LoyaltyIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { loginUser } from '../../redux/slices/authSlice';
import brandingConfig from '../../config/brandingConfig';
import { PrimaryLogo, PrimaryLogoOnDark, IconLogo, IconLogoOnDark } from '../common/BrandedImage';
import PartnerStrip from './PartnerStrip';
import StoreBadges from '../common/StoreBadges';

// ---------------------------------------------------------------------------
// Design-Bausteine
//
// Hinweis: Layouts hier nutzen bewusst CSS-Grid über <Box> statt <Grid item>.
// Ab MUI 7 ignoriert <Grid> die alten Props `item`/`xs`/`md` (nur noch `size`),
// weshalb die frühere Grid-Fassung dieser Seite faktisch ohne Spalten lief.
// ---------------------------------------------------------------------------

const DARK_BG = '#111111';

/**
 * Globales `body, html { height: 100%; overflow-x: hidden }` (App.css) macht
 * **body** zum Scroll-Container — `window.scrollY` bleibt deshalb dauerhaft 0.
 * Ohne diese Helfer würde die Kopfzeile nie ihren Scroll-Zustand bekommen und
 * das Zurücksetzen an den Seitenanfang wirkungslos bleiben.
 */
const getScrollTop = () =>
  window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

/** Das Element, das tatsächlich scrollt — meist body, sonst der Wurzelknoten. */
const getScroller = () => {
  const { body } = document;
  if (body && body.scrollHeight > body.clientHeight + 1) return body;
  return document.scrollingElement || document.documentElement;
};

const scrollPageToTop = () => {
  window.scrollTo(0, 0);
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;
};

/** Höhe der fixierten Kopfzeile — Sprungziele dürfen nicht darunter liegen. */
const HEADER_OFFSET = 72;

/**
 * Weiches Scrollen von Hand.
 *
 * `scrollIntoView({ behavior: 'smooth' })` bleibt in Chrome wirkungslos, solange
 * body der Scroll-Container ist (siehe getScrollTop) — die Sprungnavigation der
 * Seite tat schlicht nichts. Deshalb hier eine eigene rAF-Animation, die
 * `prefers-reduced-motion` respektiert.
 */
const animateScrollTo = (targetTop, duration = 550) => {
  const scroller = getScroller();
  const start = scroller.scrollTop;
  const max = scroller.scrollHeight - scroller.clientHeight;
  const target = Math.max(0, Math.min(targetTop, max));
  const distance = target - start;

  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  if (reduceMotion || Math.abs(distance) < 2) {
    scroller.scrollTop = target;
    return;
  }

  const startedAt = performance.now();
  const step = (now) => {
    const progress = Math.min((now - startedAt) / duration, 1);
    // easeInOutQuad
    const eased = progress < 0.5
      ? 2 * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    scroller.scrollTop = start + distance * eased;
    if (progress < 1) window.requestAnimationFrame(step);
  };
  window.requestAnimationFrame(step);
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'transparent',
  boxShadow: 'none',
  borderBottom: '1px solid transparent',
  transition: 'background-color .35s ease, border-color .35s ease, backdrop-filter .35s ease',
  '&.scrolled': {
    backgroundColor: alpha('#0d0d0d', 0.85),
    backdropFilter: 'blur(12px)',
    borderBottomColor: alpha(theme.palette.primary.main, 0.25),
  },
}));

/** Kleines Label über jeder Section-Überschrift. */
const Eyebrow = ({ children, tone = 'light' }) => {
  const theme = useTheme();
  const onDark = tone === 'dark';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1.75,
        py: 0.6,
        mb: 2,
        // Kein Button, sondern ein Label-Pill — bleibt bewusst rund.
        borderRadius: 999,
        border: `1px solid ${onDark ? alpha(theme.palette.primary.main, 0.35) : alpha('#000', 0.1)}`,
        bgcolor: onDark ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.16),
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
      <Typography
        variant="overline"
        sx={{
          lineHeight: 1.6,
          letterSpacing: 1.6,
          fontWeight: 700,
          color: onDark ? 'primary.main' : 'secondary.main',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
};

/** Abschnitts-Kopf: Eyebrow + Headline + Fließtext, links- oder mittig gesetzt. */
const SectionHeading = ({ eyebrow, title, subtitle, tone = 'light', align = 'center' }) => {
  const onDark = tone === 'dark';
  return (
    <Box
      sx={{
        textAlign: align,
        maxWidth: align === 'center' ? 780 : 'none',
        mx: align === 'center' ? 'auto' : 0,
        mb: { xs: 5, md: 7 },
      }}
    >
      <Eyebrow tone={tone}>{eyebrow}</Eyebrow>
      <Typography
        variant="h3"
        component="h2"
        sx={{
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: onDark ? '#fff' : 'secondary.main',
          fontSize: { xs: '2rem', md: '2.75rem' },
          mb: 2,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="h6"
          component="p"
          sx={{
            fontWeight: 400,
            lineHeight: 1.6,
            color: onDark ? alpha('#fff', 0.65) : 'text.secondary',
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

/** Karte mit Icon-Kachel, für helle und dunkle Abschnitte. */
const FeatureTile = ({ icon: Icon, title, description, tone = 'light' }) => {
  const theme = useTheme();
  const onDark = tone === 'dark';
  return (
    <Box
      sx={{
        height: '100%',
        p: { xs: 2.5, md: 3 },
        borderRadius: 4,
        border: `1px solid ${onDark ? alpha('#fff', 0.09) : alpha('#000', 0.08)}`,
        bgcolor: onDark ? alpha('#fff', 0.04) : '#fff',
        transition: 'transform .3s ease, border-color .3s ease, box-shadow .3s ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: alpha(theme.palette.primary.main, onDark ? 0.5 : 0.7),
          boxShadow: onDark ? 'none' : `0 18px 40px ${alpha('#000', 0.09)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          mb: 2,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: onDark ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.22),
        }}
      >
        <Icon sx={{ fontSize: 26, color: onDark ? 'primary.main' : 'secondary.main' }} />
      </Box>
      <Typography
        variant="h6"
        component="h3"
        sx={{ fontWeight: 700, mb: 1, color: onDark ? '#fff' : 'secondary.main' }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ lineHeight: 1.7, color: onDark ? alpha('#fff', 0.6) : 'text.secondary' }}
      >
        {description}
      </Typography>
    </Box>
  );
};

/** Aufzählung mit Häkchen — für die Detailabschnitte. */
const CheckList = ({ items, tone = 'light' }) => {
  const onDark = tone === 'dark';
  return (
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1.5 }}>
      {items.map((item) => (
        <Box component="li" key={item} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <CheckIcon sx={{ fontSize: 20, mt: '2px', color: 'primary.main', flexShrink: 0 }} />
          <Typography
            variant="body1"
            sx={{ lineHeight: 1.6, color: onDark ? alpha('#fff', 0.75) : 'text.primary' }}
          >
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * Responsives Karten-Raster ohne <Grid>.
 * `cols` erzwingt ab md eine feste Spaltenzahl — sinnvoll, wenn die Kachelzahl
 * sonst eine halb leere letzte Reihe ergibt.
 */
const CardGrid = ({ children, min = 260, gap = 3, cols }) => (
  <Box
    sx={{
      display: 'grid',
      gap,
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        md: cols ? `repeat(${cols}, minmax(0, 1fr))` : `repeat(auto-fit, minmax(${min}px, 1fr))`,
      },
    }}
  >
    {children}
  </Box>
);

/** Zweispaltiges Text/Visual-Layout. */
const SplitLayout = ({ children, reverse = false, ratio = '1fr 1fr' }) => (
  <Box
    sx={{
      display: 'grid',
      gap: { xs: 5, md: 7 },
      alignItems: 'center',
      gridTemplateColumns: { xs: '1fr', md: ratio },
      '& > *:first-of-type': { order: { xs: 1, md: reverse ? 2 : 1 } },
      '& > *:last-of-type': { order: { xs: 2, md: reverse ? 1 : 2 } },
    }}
  >
    {children}
  </Box>
);

const LoginContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '440px',
  width: '100%',
  borderRadius: theme.shape.borderRadius * 5,
  backdropFilter: 'blur(10px)',
  backgroundColor: alpha(theme.palette.background.paper, 0.92),
  boxShadow: `0 24px 60px ${alpha('#000', 0.22)}`,
}));

const Form = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
}));

// Die Eckenrundung kommt bewusst aus dem Theme (brandingConfig.ui.buttonStyle),
// damit ein Mandant mit "square" auch hier eckige Buttons bekommt.
const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
  padding: theme.spacing(1.25),
  fontWeight: 700,
}));

const FooterLink = styled(Link)(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(1),
  color: alpha('#fff', 0.65),
  textDecoration: 'none',
  transition: 'color .2s ease',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const LandingPage = ({ directLogin = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Die Navigation trägt inzwischen sieben Punkte — unterhalb von lg wandert
  // sie deshalb komplett in das Drawer-Menü.
  const isCompactNav = useMediaQuery(theme.breakpoints.down('lg'));

  const { isLoading, error } = useSelector((state) => state.auth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Store-Links wie in MobileAppRedirect aus appSettings/general — so bleiben
  // die Badges gültig, ohne dass hier URLs fest verdrahtet werden.
  const [storeUrls, setStoreUrls] = useState({ appStoreUrl: '', playStoreUrl: '' });

  // Automatisches Scrollen zum Login-Bereich für White-Label-Versionen
  const loginSectionRef = useRef(null);

  // Bestimme, ob es sich um die Default-App (NextCab) handelt
  const isDefaultApp = brandingConfig.appName === "NextCab";

  // Handle scroll event to change header appearance
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(getScrollTop() > 50);
    };

    // Auf beiden Zielen lauschen: je nach Seite scrollt mal das Fenster,
    // mal der body (siehe getScrollTop).
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.body.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Store-URLs für die App-Badges laden. Schlägt der Abruf fehl, zeigen die
  // Badges auf /mobile-app — dort läuft ohnehin die Plattformweiche.
  useEffect(() => {
    let active = true;
    getDoc(doc(db, 'appSettings', 'general'))
      .then((snapshot) => {
        if (!active || !snapshot.exists()) return;
        const data = snapshot.data();
        setStoreUrls({
          appStoreUrl: data.appStoreUrl || '',
          playStoreUrl: data.playStoreUrl || '',
        });
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Scroll-Logik basierend auf App-Typ
  useEffect(() => {
    // Bei der Standard-App (NextCab): Immer zum Anfang der Seite scrollen
    if (!directLogin && isDefaultApp) {
      // Sicherstellen, dass die Seite ganz oben beginnt
      scrollPageToTop();
    }
    // Bei White-Label-Apps: Direkt zum Login-Bereich scrollen
    else if (directLogin && loginSectionRef.current) {
      loginSectionRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [directLogin, isDefaultApp]);

  const validateEmail = (email) => {
    if (!email) {
      setEmailError('E-Mail ist erforderlich');
      return false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      setEmailError('Ungültige E-Mail-Adresse');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (password) => {
    if (!password) {
      setPasswordError('Passwort ist erforderlich');
      return false;
    } else if (password.length < 8) {
      setPasswordError('Passwort muss mindestens 8 Zeichen lang sein');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (isEmailValid && isPasswordValid) {
      try {
        const resultAction = await dispatch(loginUser({ email, password }));
        if (loginUser.fulfilled.match(resultAction)) {
          if (location.state?.returnUrl) {
            navigate(location.state.returnUrl);
          } else {
            navigate('/dashboard');
          }
        }
      } catch (error) {
        console.error('Login failed:', error);
      }
    }
  };

  const handleToggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Zentrale Funktion zum Scrollen zum Login-Bereich
  const scrollToLogin = () => {
    // Bei White-Label wird direkt das Element mit der ID "login-section" verwendet,
    // bei der Standard-App das Element mit ID "client-login"
    const loginElementId = isDefaultApp ? "client-login" : "login-section";
    smoothScrollToElement(loginElementId);
  };

  // Funktion für weiches Scrollen zu Elementen über die Navbar
  const smoothScrollToElement = (elementId) => {
    const element = document.getElementById(elementId);
    if (!element) return;
    const scroller = getScroller();
    const top = element.getBoundingClientRect().top + scroller.scrollTop - HEADER_OFFSET;
    animateScrollTo(top);
  };

  // Navbar items
  const navItems = [
    { text: 'Startseite', href: '#home' },
    { text: 'Features', href: '#features' },
    { text: 'Krankenfahrten', href: '#krankenfahrten' },
    { text: 'KI-Telefonist', href: '#ki-telefonist' },
    { text: 'Mobile App', href: '#mobile-app' },
    { text: 'White Label', href: '#white-label' },
    { text: 'Kontakt', href: '#contact' },
  ];

  // Feature items
  const features = [
    {
      icon: DashboardIcon,
      title: "Übersichtliches Dashboard",
      description: "Behalten Sie den Überblick über alle laufenden Aktivitäten Ihres Betriebs auf einen Blick."
    },
    {
      icon: CarsIcon,
      title: "Fahrzeugverwaltung",
      description: "Verwalten Sie Ihre Fahrzeugflotte mit detaillierten Informationen zu jedem Fahrzeug."
    },
    {
      icon: PeopleIcon,
      title: "Fahrerverwaltung",
      description: "Organisieren Sie Ihre Fahrer, deren Profile, Dokumente und Verfügbarkeiten."
    },
    {
      icon: RouteIcon,
      title: "Disposition & Fahrten",
      description: "Sofortfahrten, Vorbestellungen und Daueraufträge an einer Stelle. Die Zuteilung läuft automatisch, eingreifen können Sie jederzeit."
    },
    {
      icon: ScheduleIcon,
      title: "Schichtplanung",
      description: "Erstellen Sie effiziente Schichtpläne für Ihre Fahrer mit Kalenderansicht."
    },
    {
      icon: ReceiptIcon,
      title: "Quittungen & Rechnungen",
      description: "Die Quittung geht nach der Fahrt automatisch an den Fahrgast. Firmenkunden bekommen eine monatliche Sammelrechnung oder eine Einzelrechnung je Fahrt."
    },
    {
      icon: LoyaltyIcon,
      title: "Treueprogramm",
      description: "Wahlweise Treuekarte (jede fünfte Fahrt vergünstigt) oder Treuemeilen je gefahrenem Kilometer. Schwellen, Rabatt und Laufzeit legen Sie selbst fest."
    },
    {
      icon: AnalyticsIcon,
      title: "Berichte & Analysen",
      description: "Generieren Sie umfassende Berichte für fundierte Geschäftsentscheidungen."
    },
    {
      icon: TuneIcon,
      title: "Auf Ihren Betrieb eingestellt",
      description: "Von Festpreisen über Vorlaufzeiten bis zur Verkehrsform (Taxi oder Mietwagen nach § 49 PBefG): Sie stellen ein, wie Ihr Betrieb tatsächlich arbeitet."
    }
  ];

  // Mobile App Features
  const mobileFeatures = [
    {
      icon: LocationIcon,
      title: "Live-Tracking",
      description: "Kunden können Ihre Fahrer in Echtzeit verfolgen und erhalten genaue Ankunftszeiten."
    },
    {
      icon: EventIcon,
      title: "Vorbestellungen",
      description: "Einfache Planung von Fahrten im Voraus mit Kalenderintegration und Erinnerungen."
    },
    {
      icon: SmartphoneIcon,
      title: "Benutzerfreundliche App",
      description: "Intuitive Benutzeroberfläche für Kunden und Fahrer auf iOS und Android."
    },
    {
      icon: SpeedIcon,
      title: "Schnelle Bestellung",
      description: "Taxi mit wenigen Klicks bestellen oder Favoriten für regelmäßige Strecken speichern."
    }
  ];

  // Krankenfahrten: der Weg von der Verordnung bis zum Geldeingang
  const medicalSteps = [
    {
      icon: ScannerIcon,
      title: "Verordnung erfassen",
      description: "Muster 4 abfotografieren — die Texterkennung füllt Patient, Krankenkasse, IK-Nummer, Verordnungs- und Genehmigungsdaten vor. Das geht in der Zentrale und genauso in der Fahrer-App direkt am Einstieg."
    },
    {
      icon: ApprovalIcon,
      title: "Genehmigung & Kontingent",
      description: "Gültigkeitszeitraum, Genehmigungspflicht und Restfahrten laufen automatisch mit. Das Portal warnt, bevor eine Verordnung ausläuft oder das Kontingent aufgebraucht ist."
    },
    {
      icon: SignatureIcon,
      title: "Fahrt & Nachweis",
      description: "Der Fahrer sieht den Sonderbedarf, lässt direkt auf dem Gerät unterschreiben — und die besetzten Kilometer kommen aus dem Fahrtentracking statt aus der Erinnerung."
    },
    {
      icon: BillingIcon,
      title: "Abrechnung",
      description: "Fertige Fahrten gehen als DMRZ-Fahrtenimport oder an opta data / CareMan. Der Status von „bereit“ bis „bezahlt“ bleibt an jeder Fahrt sichtbar."
    }
  ];

  const medicalHighlights = [
    'Jede Verordnung ist ein eigener Fall mit Patient, Kasse, Genehmigung, Zeitraum und Kontingent — nicht nur ein Haken an der Fahrt.',
    'Der Fahrer scannt die Verordnung beim Einsteigen selbst und sieht sofort als Checkliste, ob etwas fehlt. Die Zentrale muss nichts mehr abtippen und nichts hinterhertelefonieren.',
    'Vollständigkeitsprüfung schon bei der Erfassung: fehlender Stempel, fehlende Unterschrift oder fehlende Genehmigung fallen nicht erst bei der Abrechnung auf.',
    'Zuzahlung nach § 61 SGB V wird dem richtigen Beleg zugeordnet und taucht bei Hin- und Rückfahrt nicht doppelt auf.',
    'Vergütungsvereinbarungen nach § 133 SGB V hinterlegen und schon in der Disposition sehen, was eine Fahrt einbringt.',
    'Daueraufträge für Dialyse, Chemo oder Therapie: einmal anlegen, alle Termine stehen im Plan.',
    'Verordnung abgelaufen? Mit „Erneuern“ bleiben Stammdaten und Historie erhalten, nur die neuen Verordnungsfelder werden erfasst.'
  ];

  // KI-Telefonist: was der Assistent im Gespräch tatsächlich erledigt
  const aiPhoneCapabilities = [
    'Nimmt rund um die Uhr in natürlichem Deutsch ab — nachts, am Wochenende und wenn alle Leitungen belegt sind.',
    'Erkennt Stammkunden an der Rufnummer und schlägt die gewohnte Abholadresse vor.',
    'Bucht Sofortfahrten und Vorbestellungen direkt in Ihre Disposition — kein Abtippen, keine Insellösung.',
    'Versteht Geschäfte und Orte als Adresse, dazu Großraumwagen, Gepäck und Kartenzahlung.',
    'Nennt Wartezeit, Festpreis und geschätzten Fahrpreis.',
    'Storniert und ändert bestehende Fahrten des Anrufers.',
    'Übergibt Krankenfahrten, Beschwerden und Sonderfälle grundsätzlich an einen Menschen.'
  ];

  const aiPhoneControls = [
    {
      icon: BoltIcon,
      title: "Sie bestimmen den Umfang",
      description: "Buchen, Stornieren, Ändern und Festpreis-Auskunft lassen sich einzeln freigeben oder sperren. Gesperrtes übergibt die KI an einen Menschen."
    },
    {
      icon: PhoneInTalkIcon,
      title: "Empfang oder Überlauf",
      description: "Die KI übernimmt komplett oder springt nur ein, wenn niemand abnimmt. Fällt sie aus, greift die klassische Weiterleitung."
    },
    {
      icon: ShieldIcon,
      title: "EU-Verarbeitung, transparent",
      description: "Betrieb in Frankfurt, Anrufer erfahren, dass sie mit einer KI sprechen — das erfüllt die Transparenzpflicht des EU AI Act ab dem 02.08.2026."
    },
    {
      icon: EuroIcon,
      title: "Rechnet sich ab dem ersten Anruf",
      description: "Jeder unbeantwortete Anruf ist eine Fahrt, die ein Wettbewerber fährt. Die KI nimmt genau die Anrufe an, für die sich keine Nachtschicht lohnt."
    }
  ];

  return (
    <>
      {/* Navigation - nur für NextCab anzeigen oder wenn directLogin=false */}
      {!directLogin && (
        <StyledAppBar position="fixed" className={scrolled ? 'scrolled' : ''}>
          <Toolbar sx={{ height: 72, overflow: 'hidden' }}>
            <Box display="flex" alignItems="center" flexGrow={1} sx={{ maxHeight: 72, overflow: 'hidden' }}>
              <IconLogoOnDark
                alt={`${brandingConfig.appName} Logo`}
                style={{ height: 44, marginRight: 0 }}
              />
              {/* Kopfzeile liegt über dunklem Hero bzw. dunklem Blur — helle Logovariante */}
              <PrimaryLogoOnDark
                alt={brandingConfig.appName}
                style={{ height: 130, marginLeft: -4 }}
              />
            </Box>

            {/* Navigations-Tabs nur für Default NextCab App anzeigen */}
            {isDefaultApp && !isCompactNav && (
              <Box display="flex" alignItems="center">
                {navItems.map((item) => (
                  <Button
                    key={item.text}
                    color="inherit"
                    onClick={() => smoothScrollToElement(item.href.substring(1))}
                    sx={{
                      mx: 0.4,
                      color: alpha('#fff', 0.8),
                      fontWeight: 500,
                      '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={scrollToLogin}
                  sx={{ ml: 2, px: 3, fontWeight: 700 }}
                >
                  Anmelden
                </Button>
              </Box>
            )}

            {/* Bei White-Label-Versionen nur den Login-Button anzeigen */}
            {!isDefaultApp && !isMobile && (
              <Box display="flex">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={scrollToLogin}
                  sx={{ ml: 2, px: 3, fontWeight: 700 }}
                >
                  Anmelden
                </Button>
              </Box>
            )}

            {((isDefaultApp && isCompactNav) || (!isDefaultApp && isMobile)) && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleToggleDrawer}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </StyledAppBar>
      )}

      {/* Mobile Drawer - nur anzeigen, wenn Navigation sichtbar ist */}
      {!directLogin && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleToggleDrawer}
        >
          <Box sx={{ width: 270 }} role="presentation">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconLogo
                  alt={`${brandingConfig.appName} Logo`}
                  style={{ height: 30, marginRight: 0 }}
                />
                <PrimaryLogo
                  alt={brandingConfig.appName}
                  style={{ height: 70, marginLeft: -2 }}
                />
              </Box>
              <IconButton onClick={handleToggleDrawer}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
            <List>
              {/* Navigation Items nur für Default App (NextCab) anzeigen */}
              {isDefaultApp && navItems.map((item) => (
                <ListItem
                  button
                  key={item.text}
                  onClick={() => {
                    smoothScrollToElement(item.href.substring(1));
                    handleToggleDrawer();
                  }}
                >
                  <ListItemText primary={item.text} />
                </ListItem>
              ))}
              <ListItem
                button
                onClick={() => {
                  scrollToLogin();
                  handleToggleDrawer();
                }}
              >
                <ListItemText primary="Anmelden" />
              </ListItem>

              {/* Rechtliche Seiten im Menü */}
              <Divider sx={{ my: 1 }} />
              <ListItem
                button
                component={RouterLink}
                to="/datenschutz"
                onClick={handleToggleDrawer}
              >
                <ListItemText primary="Datenschutz" />
              </ListItem>
              <ListItem
                button
                component={RouterLink}
                to="/impressum"
                onClick={handleToggleDrawer}
              >
                <ListItemText primary="Impressum" />
              </ListItem>
            </List>
          </Box>
        </Drawer>
      )}

      {/* Hero Section - nur für NextCab und wenn nicht directLogin */}
      {(!directLogin && isDefaultApp) && (
        <Box
          id="home"
          sx={{
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            px: 3,
            pt: { xs: 14, md: 12 },
            pb: { xs: 10, md: 12 },
            overflow: 'hidden',
            bgcolor: DARK_BG,
            color: '#fff',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage: `
                radial-gradient(circle at 18% 12%, ${alpha(theme.palette.primary.main, 0.22)} 0%, transparent 48%),
                radial-gradient(circle at 85% 78%, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 45%),
                linear-gradient(${alpha('#fff', 0.035)} 1px, transparent 1px),
                linear-gradient(90deg, ${alpha('#fff', 0.035)} 1px, transparent 1px)
              `,
              backgroundSize: '100% 100%, 100% 100%, 64px 64px, 64px 64px',
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Fade in timeout={900}>
              <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
                <Eyebrow tone="dark">Disposition · Krankenfahrten · KI-Telefonie</Eyebrow>
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.05,
                    fontSize: { xs: '2.6rem', sm: '3.6rem', md: '4.5rem' },
                    mb: 3,
                  }}
                >
                  Die Zentrale, die{' '}
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    fast von allein läuft
                  </Box>
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    fontWeight: 400,
                    lineHeight: 1.7,
                    color: alpha('#fff', 0.7),
                    maxWidth: 720,
                    mx: 'auto',
                    mb: 5,
                  }}
                >
                  Anruf annehmen, Fahrt zuteilen, Quittung verschicken, Krankenfahrt abrechnen:
                  {' '}{brandingConfig.appName} übernimmt die Handgriffe, die Ihre Zentrale den
                  ganzen Tag beschäftigen — für Taxi- und Mietwagenunternehmen.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    justifyContent: 'center',
                    mb: 7,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => smoothScrollToElement('krankenfahrten')}
                    sx={{ px: 4, py: 1.5, fontWeight: 700 }}
                  >
                    Features ansehen
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={scrollToLogin}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      color: '#fff',
                      borderColor: alpha('#fff', 0.4),
                      '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.08) },
                    }}
                  >
                    Zum Admin-Portal
                  </Button>
                </Box>

                {/* Kurzüberblick statt Marketing-Zahlen: was die Plattform abdeckt */}
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                    maxWidth: 900,
                    mx: 'auto',
                  }}
                >
                  {[
                    { icon: RouteIcon, label: 'Disposition & Live-Karte' },
                    { icon: MedicalIcon, label: 'Krankenfahrten & Abrechnung' },
                    { icon: AgentIcon, label: 'KI-Telefonist rund um die Uhr' },
                    { icon: SmartphoneIcon, label: 'Apps für Kunden & Fahrer' },
                  ].map(({ icon: Icon, label }) => (
                    <Box
                      key={label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.75,
                        borderRadius: 3,
                        border: `1px solid ${alpha('#fff', 0.1)}`,
                        bgcolor: alpha('#fff', 0.03),
                        textAlign: 'left',
                      }}
                    >
                      <Icon sx={{ color: 'primary.main', fontSize: 22 }} />
                      <Typography variant="body2" sx={{ color: alpha('#fff', 0.75), fontWeight: 500 }}>
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Fade>
          </Container>
        </Box>
      )}

      {/* Für White-Label Apps (nicht NextCab) und directLogin=false: Vereinfachter Header */}
      {(!directLogin && !isDefaultApp) && (
        <Box
          id="home"
          sx={{
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: 3,
            pt: 12,
            pb: 8,
            bgcolor: DARK_BG,
            color: '#fff',
          }}
        >
          <Fade in timeout={900}>
            <Box>
              <Typography variant="h1" component="h1" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                {brandingConfig.appName}
              </Typography>
              <Typography variant="h5" component="h2" sx={{ mb: 4, color: alpha('#fff', 0.7) }}>
                Admin Portal
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={scrollToLogin}
                sx={{ px: 4, py: 1.5, fontWeight: 700 }}
              >
                Zum Login
              </Button>
            </Box>
          </Fade>
        </Box>
      )}

      {/* Features Section - nur für Default App (NextCab) und nicht directLogin */}
      {(!directLogin && isDefaultApp) && (
        <Box id="features" sx={{ py: { xs: 8, md: 12 }, px: 3, bgcolor: '#fafafa' }}>
          <Container maxWidth="lg">
            <SectionHeading
              eyebrow="Hauptfunktionen"
              title="Der gesamte Betrieb in einer Oberfläche"
              subtitle="Alles, was Sie für die effiziente Verwaltung Ihres Betriebs benötigen — ohne Medienbruch zwischen Zettel, Telefon und Buchhaltung."
            />
            <CardGrid cols={3}>
              {features.map((feature) => (
                <FeatureTile key={feature.title} {...feature} />
              ))}
            </CardGrid>
          </Container>
        </Box>
      )}

      {/* Krankenfahrten-System - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <Box
          id="krankenfahrten"
          sx={{
            py: { xs: 8, md: 12 },
            px: 3,
            bgcolor: DARK_BG,
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at 80% 0%, ${alpha(theme.palette.primary.main, 0.14)} 0%, transparent 55%)`,
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <SectionHeading
              tone="dark"
              eyebrow="Krankenfahrten-System"
              title="Vom Rezept bis zum Geldeingang"
              subtitle="Kassenfahrten sind planbarer Umsatz — aber nur, wenn der Beleg stimmt. Eine beanstandete Fahrt kostet Sie den Fahrpreis und die Nacharbeit. NextCab führt jede Verordnung als eigenen Fall, prüft die Vollständigkeit schon bei der Erfassung und übergibt sauber an Ihr Abrechnungszentrum."
            />

            {/* Prozesskette */}
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                mb: { xs: 6, md: 8 },
              }}
            >
              {medicalSteps.map((step, index) => (
                <Box key={step.title} sx={{ position: 'relative' }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: 2,
                      color: alpha(theme.palette.primary.main, 0.85),
                      mb: 1.5,
                    }}
                  >
                    SCHRITT {index + 1}
                  </Typography>
                  <FeatureTile tone="dark" {...step} />
                </Box>
              ))}
            </Box>

            <SplitLayout ratio="1.1fr 0.9fr">
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#fff' }}>
                  Was das im Alltag bedeutet
                </Typography>
                <CheckList tone="dark" items={medicalHighlights} />
                <Box
                  sx={{
                    mt: 4,
                    p: 2.5,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.07),
                  }}
                >
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), lineHeight: 1.7 }}>
                    <strong>Abrechnung ohne Systemwechsel:</strong> Der Export erzeugt den
                    DMRZ-Fahrtenimport nach § 302 SGB V oder eine Datei für opta data / CareMan.
                    Dazu Nachweis-PDF und Übersichts-CSV für Ihre eigene Ablage.
                  </Typography>
                </Box>
              </Box>

              {/* Mockup: Verordnungsfall im Portal */}
              <Box
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: `1px solid ${alpha('#fff', 0.12)}`,
                  bgcolor: '#1b1b1b',
                  boxShadow: `0 30px 70px ${alpha('#000', 0.5)}`,
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: `1px solid ${alpha('#fff', 0.1)}`,
                  }}
                >
                  <MedicalIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, flex: 1 }}>
                    Verordnung · Muster 4
                  </Typography>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.35,
                      borderRadius: 999,
                      bgcolor: alpha(theme.palette.success.main, 0.18),
                      color: theme.palette.success.light,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                    }}
                  >
                    GENEHMIGT
                  </Box>
                </Box>

                <Box sx={{ p: 2.5, display: 'grid', gap: 1.25 }}>
                  {[
                    { label: 'Patient', value: 'M. Schneider, 14.03.1948' },
                    { label: 'Krankenkasse', value: 'AOK · IK 101317013' },
                    { label: 'Behandlung', value: 'Dialyse · feste Strecke' },
                    { label: 'Zeitraum', value: '01.07. – 30.09.2026' },
                  ].map((row) => (
                    <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.45) }}>
                        {row.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.85), fontWeight: 600, textAlign: 'right' }}>
                        {row.value}
                      </Typography>
                    </Box>
                  ))}

                  {/* Kontingent-Balken */}
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.45) }}>
                        Kontingent
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        18 von 24 Fahrten
                      </Typography>
                    </Box>
                    <Box sx={{ height: 7, borderRadius: 999, bgcolor: alpha('#fff', 0.1), overflow: 'hidden' }}>
                      <Box sx={{ width: '75%', height: '100%', bgcolor: 'primary.main' }} />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.5, borderColor: alpha('#fff', 0.08) }} />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {[
                      { text: 'Scan hinterlegt', color: theme.palette.info.light },
                      { text: 'Unterschrift vollständig', color: theme.palette.success.light },
                      { text: 'Zuzahlung befreit', color: alpha('#fff', 0.6) },
                      { text: 'Bereit zur Abrechnung', color: theme.palette.primary.main },
                    ].map((chip) => (
                      <Box
                        key={chip.text}
                        sx={{
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 999,
                          border: `1px solid ${alpha(chip.color, 0.4)}`,
                          color: chip.color,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                        }}
                      >
                        {chip.text}
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: 2.5,
                    py: 1.75,
                    borderTop: `1px solid ${alpha('#fff', 0.1)}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: alpha('#fff', 0.03),
                  }}
                >
                  <RecurringIcon sx={{ color: alpha('#fff', 0.5), fontSize: 18 }} />
                  <Typography variant="caption" sx={{ color: alpha('#fff', 0.55) }}>
                    Dauerauftrag aktiv: Mo · Mi · Fr, 07:30 Uhr
                  </Typography>
                </Box>
              </Box>
            </SplitLayout>
          </Container>
        </Box>
      )}

      {/* KI-Telefonist - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <Box id="ki-telefonist" sx={{ py: { xs: 8, md: 12 }, px: 3, bgcolor: '#fafafa' }}>
          <Container maxWidth="lg">
            <SectionHeading
              eyebrow="KI-Telefonist"
              title="Besetzt ist der teuerste Zustand Ihrer Zentrale"
              subtitle="Ein deutschsprachiger Assistent nimmt ab, wenn bei Ihnen gerade niemand kann — und legt die Fahrt direkt in Ihrer Disposition an. Kein zweites System, keine Liste, die jemand abtippen muss."
            />

            <SplitLayout ratio="0.9fr 1.1fr" reverse>
              {/* Mockup: Gesprächsverlauf */}
              <Box
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: `1px solid ${alpha('#000', 0.09)}`,
                  bgcolor: '#fff',
                  boxShadow: `0 24px 60px ${alpha('#000', 0.1)}`,
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: 'secondary.main',
                    color: '#fff',
                  }}
                >
                  <PhoneInTalkIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      Eingehender Anruf
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                      +49 41 21 · 02:47 Uhr
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.35,
                      borderRadius: 999,
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      color: 'primary.main',
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  >
                    KI AKTIV
                  </Box>
                </Box>

                <Box sx={{ p: 2.5, display: 'grid', gap: 1.5, bgcolor: '#f7f7f7' }}>
                  {[
                    { from: 'ai', text: `${brandingConfig.appName}, guten Abend — Sie sprechen mit einem digitalen Assistenten. Wohin darf es gehen?` },
                    { from: 'caller', text: 'Zum Bahnhof bitte, so schnell wie möglich.' },
                    { from: 'ai', text: 'Gern. Hole ich Sie wie sonst in der Lieth 2d ab?' },
                    { from: 'caller', text: 'Genau.' },
                    { from: 'ai', text: 'Der Wagen ist in etwa 8 Minuten bei Ihnen. Ich habe die Fahrt angelegt.' },
                  ].map((line, index) => (
                    <Box
                      key={index}
                      sx={{
                        alignSelf: line.from === 'ai' ? 'flex-start' : 'flex-end',
                        maxWidth: '88%',
                        px: 1.75,
                        py: 1.25,
                        borderRadius: 2.5,
                        borderTopLeftRadius: line.from === 'ai' ? 4 : 20,
                        borderTopRightRadius: line.from === 'ai' ? 20 : 4,
                        bgcolor: line.from === 'ai' ? 'secondary.main' : '#fff',
                        color: line.from === 'ai' ? alpha('#fff', 0.92) : 'text.primary',
                        border: line.from === 'ai' ? 'none' : `1px solid ${alpha('#000', 0.08)}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ lineHeight: 1.55, fontSize: '0.85rem' }}>
                        {line.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    p: 2.5,
                    borderTop: `1px solid ${alpha('#000', 0.08)}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.22),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <CarsIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Fahrt #4821 angelegt
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Lieth 2d → Bahnhof Elmshorn · Stammkunde erkannt
                    </Typography>
                  </Box>
                  <CheckIcon sx={{ color: 'success.main' }} />
                </Box>
              </Box>

              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'secondary.main' }}>
                  Was der Assistent im Gespräch erledigt
                </Typography>
                <CheckList items={aiPhoneCapabilities} />
              </Box>
            </SplitLayout>

            <Box sx={{ mt: { xs: 6, md: 8 } }}>
              <CardGrid min={250}>
                {aiPhoneControls.map((item) => (
                  <FeatureTile key={item.title} {...item} />
                ))}
              </CardGrid>
            </Box>

            {/* CTI-Brücke: der Mensch bleibt Teil des Systems */}
            <Box
              sx={{
                mt: 4,
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                bgcolor: 'secondary.main',
                color: '#fff',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <PhoneIcon sx={{ fontSize: 36, color: 'primary.main' }} />
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Und wenn ein Mensch abnimmt?
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#fff', 0.72), lineHeight: 1.7 }}>
                  Der Connector für die AVM FRITZ!Box meldet eingehende Anrufe an das Portal.
                  Ihr Disponent sieht Rufnummer und passenden Stammkunden, bevor er „Hallo“ sagt —
                  die Fahrt ist mit wenigen Tasten erfasst und bleibt mit dem Anruf verknüpft.
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* Live Tracking Demo - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <Box sx={{ position: 'relative', overflow: 'hidden', py: { xs: 8, md: 12 }, px: 3, bgcolor: '#fff' }}>
          <Container maxWidth="lg">
            <SplitLayout ratio="0.85fr 1.15fr">
              <Box>
                <Eyebrow>Disposition in Echtzeit</Eyebrow>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 2, color: 'secondary.main' }}
                >
                  Die Fahrt findet ihren Fahrer
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  Position, Status und aktuelle Fahrt jedes Fahrers laufen in Echtzeit auf der
                  Karte zusammen. Die Zuteilung übernimmt das System — Ihr Disponent greift nur
                  noch ein, wenn er es will.
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <CheckList
                    items={[
                      'Vorbestellungen werden rechtzeitig disponiert: Bei Abholungen außerhalb Ihres Betriebsgebiets rechnet das System die Anfahrtszeit ein statt eines starren Zeitfensters.',
                      'Festpreislisten für Ihre häufigen Strecken — der Preis steht schon bei der Annahme fest, am Telefon wie in der App.',
                      'Sofortfahrten, Vorbestellungen, Daueraufträge und Schichten in derselben Oberfläche, ohne Fensterwechsel.',
                    ]}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  onClick={scrollToLogin}
                  sx={{ mt: 1, px: 3.5, py: 1.25, fontWeight: 700 }}
                >
                  Jetzt testen
                </Button>
              </Box>

              {/* CSS Monitor Mockup */}
              <Box
                sx={{
                  transform: 'perspective(1000px) rotateY(-8deg) rotateX(4deg)',
                  transition: 'transform 0.5s ease',
                  '&:hover': {
                    transform: 'perspective(1000px) rotateY(-3deg) rotateX(1deg)',
                  },
                }}
              >
                {/* Monitor Frame */}
                <Box sx={{
                  border: '3px solid #1a1a1a',
                  borderBottom: 'none',
                  borderRadius: '12px 12px 0 0',
                  bgcolor: '#1a1a1a',
                  overflow: 'hidden',
                  boxShadow: 8,
                }}>
                  {/* Browser Chrome */}
                  <Box sx={{
                    height: 32,
                    bgcolor: '#2d2d2d',
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    gap: 0.8,
                  }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff5f57' }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#febc2e' }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#28c840' }} />
                    <Box sx={{
                      flex: 1,
                      height: 20,
                      bgcolor: '#3a3a3a',
                      borderRadius: 1,
                      ml: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                    }}>
                      <Typography variant="caption" sx={{ color: '#888', fontSize: '0.6rem' }}>
                        app.nextcab.de/dashboard
                      </Typography>
                    </Box>
                  </Box>
                  {/* App Content */}
                  <Box sx={{ display: 'flex', height: 320 }}>
                    {/* Sidebar */}
                    <Box sx={{
                      width: 56,
                      bgcolor: theme.palette.secondary.main,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      py: 1.5,
                      gap: 1,
                    }}>
                      <Box sx={{ width: 28, height: 28, mb: 1 }}>
                        <IconLogo
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </Box>
                      {[DashboardIcon, CarsIcon, PeopleIcon, RouteIcon, MedicalIcon, AnalyticsIcon].map((Icon, i) => (
                        <Box key={i} sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: i === 0 ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                        }}>
                          <Icon sx={{ fontSize: 16, color: i === 0 ? theme.palette.primary.main : '#888' }} />
                        </Box>
                      ))}
                    </Box>
                    {/* Main Area */}
                    <Box sx={{ flex: 1, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
                      {/* Top Bar */}
                      <Box sx={{
                        height: 40,
                        bgcolor: '#fff',
                        borderBottom: '1px solid #e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#333' }}>Dashboard</Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Box sx={{ width: 50, height: 8, bgcolor: '#eee', borderRadius: 1 }} />
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PeopleIcon sx={{ fontSize: 12, color: '#000' }} />
                          </Box>
                        </Box>
                      </Box>
                      {/* Dashboard Content */}
                      <Box sx={{ flex: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* KPI Row */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {[
                            { label: 'Fahrer aktiv', value: '12', color: theme.palette.success.main },
                            { label: 'Fahrten heute', value: '47', color: theme.palette.primary.main },
                            { label: 'Krankenfahrten', value: '9', color: theme.palette.info.main },
                            { label: 'Umsatz', value: '€2.4k', color: theme.palette.warning.main },
                          ].map((kpi, i) => (
                            <Box key={i} sx={{
                              flex: 1,
                              bgcolor: '#fff',
                              borderRadius: 1,
                              p: 1,
                              borderLeft: `3px solid ${kpi.color}`,
                            }}>
                              <Typography sx={{ fontSize: '0.5rem', color: '#999' }}>{kpi.label}</Typography>
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#333' }}>{kpi.value}</Typography>
                            </Box>
                          ))}
                        </Box>
                        {/* Map + Table Row */}
                        <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                          {/* Mini Map */}
                          <Box sx={{
                            flex: 1.2,
                            bgcolor: '#fff',
                            borderRadius: 1,
                            overflow: 'hidden',
                            position: 'relative',
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                          }}>
                            <Box sx={{ position: 'absolute', top: 6, left: 8 }}>
                              <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, color: '#555' }}>Live-Karte</Typography>
                            </Box>
                            {/* Grid lines */}
                            {[...Array(4)].map((_, i) => (
                              <Box key={`mh-${i}`} sx={{ position: 'absolute', left: 0, right: 0, top: `${25 * (i + 1)}%`, height: 1, bgcolor: alpha('#000', 0.04) }} />
                            ))}
                            {[...Array(5)].map((_, i) => (
                              <Box key={`mv-${i}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${16.6 * (i + 1)}%`, width: 1, bgcolor: alpha('#000', 0.04) }} />
                            ))}
                            {/* Driver dots */}
                            {[
                              { top: '30%', left: '25%' },
                              { top: '55%', left: '60%' },
                              { top: '40%', left: '45%' },
                              { top: '70%', left: '30%' },
                              { top: '25%', left: '75%' },
                            ].map((pos, i) => (
                              <Box key={`dot-${i}`} sx={{
                                position: 'absolute',
                                ...pos,
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: theme.palette.primary.main,
                                border: '2px solid #fff',
                                boxShadow: 1,
                              }} />
                            ))}
                          </Box>
                          {/* Table */}
                          <Box sx={{
                            flex: 0.8,
                            bgcolor: '#fff',
                            borderRadius: 1,
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column',
                          }}>
                            <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, color: '#555', mb: 0.5 }}>Aktuelle Fahrten</Typography>
                            {[
                              { status: theme.palette.success.main },
                              { status: theme.palette.primary.main },
                              { status: theme.palette.info.main },
                              { status: theme.palette.success.main },
                              { status: theme.palette.warning.main },
                            ].map((row, i) => (
                              <Box key={`row-${i}`} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                py: 0.4,
                                borderBottom: '1px solid #f0f0f0',
                              }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: row.status }} />
                                <Box sx={{ flex: 1, height: 6, bgcolor: '#eee', borderRadius: 0.5 }} />
                                <Box sx={{ width: 24, height: 6, bgcolor: '#eee', borderRadius: 0.5 }} />
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {/* Monitor Stand */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{
                    width: '100%',
                    height: 6,
                    bgcolor: '#1a1a1a',
                    borderRadius: '0 0 4px 4px',
                  }} />
                  <Box sx={{
                    width: 80,
                    height: 40,
                    background: 'linear-gradient(180deg, #2a2a2a, #1a1a1a)',
                  }} />
                  <Box sx={{
                    width: 140,
                    height: 8,
                    bgcolor: '#1a1a1a',
                    borderRadius: '0 0 8px 8px',
                  }} />
                </Box>
              </Box>
            </SplitLayout>
          </Container>
        </Box>
      )}

      {/* Mobile App Section - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <Box id="mobile-app" sx={{ py: { xs: 8, md: 12 }, px: 3, bgcolor: '#fafafa' }}>
          <Container maxWidth="lg">
            <SplitLayout>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                {/* CSS Smartphone Mockup */}
                <Box
                  sx={{
                    width: 280,
                    height: 500,
                    borderRadius: '36px',
                    border: '4px solid #1a1a1a',
                    bgcolor: '#1a1a1a',
                    overflow: 'hidden',
                    boxShadow: 10,
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Status Bar */}
                  <Box sx={{
                    height: 44,
                    bgcolor: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                  }}>
                    <Typography variant="caption" sx={{ color: '#000', fontWeight: 600, fontSize: '0.7rem' }}>9:41</Typography>
                    <Box sx={{ width: 80, height: 24, borderRadius: '12px', bgcolor: '#000' }} />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Box sx={{ width: 16, height: 10, borderRadius: 1, bgcolor: '#000' }} />
                      <Box sx={{ width: 16, height: 10, borderRadius: 1, bgcolor: '#000' }} />
                    </Box>
                  </Box>
                  {/* App Header */}
                  <Box sx={{
                    height: 56,
                    bgcolor: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                  }}>
                    <Typography variant="subtitle2" sx={{ color: '#000', fontWeight: 700 }}>{brandingConfig.appName}</Typography>
                  </Box>
                  {/* App Content */}
                  <Box sx={{ flex: 1, bgcolor: '#f5f5f5', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Map Area */}
                    <Box sx={{
                      flex: 1,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      <LocationIcon sx={{ fontSize: 40, color: theme.palette.primary.main, opacity: 0.6 }} />
                      {/* Street grid lines */}
                      {[...Array(5)].map((_, i) => (
                        <Box key={`h-${i}`} sx={{ position: 'absolute', left: 0, right: 0, top: `${20 * (i + 1)}%`, height: 1, bgcolor: alpha(theme.palette.primary.main, 0.08) }} />
                      ))}
                      {[...Array(4)].map((_, i) => (
                        <Box key={`v-${i}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${25 * (i + 1)}%`, width: 1, bgcolor: alpha(theme.palette.primary.main, 0.08) }} />
                      ))}
                    </Box>
                    {/* Booking Card */}
                    <Box sx={{
                      bgcolor: '#fff',
                      borderRadius: 2,
                      p: 1.5,
                      boxShadow: 2,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'green' }} />
                        <Box sx={{ flex: 1, height: 10, bgcolor: '#eee', borderRadius: 1 }} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'red' }} />
                        <Box sx={{ flex: 1, height: 10, bgcolor: '#eee', borderRadius: 1 }} />
                      </Box>
                    </Box>
                    {/* CTA Button */}
                    <Box sx={{
                      bgcolor: theme.palette.primary.main,
                      borderRadius: 2,
                      py: 1.5,
                      textAlign: 'center',
                    }}>
                      <Typography variant="caption" sx={{ color: '#000', fontWeight: 700, fontSize: '0.75rem' }}>Taxi bestellen</Typography>
                    </Box>
                  </Box>
                  {/* Bottom Nav */}
                  <Box sx={{
                    height: 50,
                    bgcolor: '#fff',
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    px: 1,
                  }}>
                    <LocationIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                    <EventIcon sx={{ fontSize: 20, color: '#bbb' }} />
                    <RouteIcon sx={{ fontSize: 20, color: '#bbb' }} />
                    <PeopleIcon sx={{ fontSize: 20, color: '#bbb' }} />
                  </Box>
                  {/* Home Indicator */}
                  <Box sx={{ height: 20, bgcolor: '#1a1a1a', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{ width: 100, height: 4, borderRadius: 2, bgcolor: '#555' }} />
                  </Box>
                </Box>
                {/* Gradient background behind phone */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -15,
                    right: -15,
                    bottom: -15,
                    left: -15,
                    borderRadius: 5,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    zIndex: 0,
                  }}
                />
              </Box>

              <Box>
                <Eyebrow>Mobile Apps</Eyebrow>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 2, color: 'secondary.main' }}
                >
                  Für Kunden und Fahrer
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 4 }}>
                  Je eine App für Ihre Kunden und Ihre Fahrer — für iOS und Android, unter Ihrem
                  Namen und Ihrem Logo. Ein Taxi-Erlebnis, das mit den großen Anbietern mithält.
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  {mobileFeatures.map((feature) => {
                    const Icon = feature.icon;
                    return (
                      <Box key={feature.title} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(theme.palette.primary.main, 0.22),
                          }}
                        >
                          <Icon sx={{ fontSize: 22, color: 'secondary.main' }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {feature.description}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Kurzform: Der Hinweis „Ihre App bald bei“ sagt genug — dass in
                    den Stores die gebrandete Mandanten-App liegt und nicht die
                    NextCab-App, erklärt ausführlich die Partnerleiste am Seitenende. */}
                <Box
                  sx={{
                    mt: 4,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: 'secondary.main', whiteSpace: 'nowrap' }}
                  >
                    Ihre App bald bei
                  </Typography>
                  <StoreBadges
                    appStoreUrl={storeUrls.appStoreUrl}
                    playStoreUrl={storeUrls.playStoreUrl}
                    height={48}
                  />
                </Box>
              </Box>
            </SplitLayout>
          </Container>
        </Box>
      )}

      {/* White Label Section - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <Box id="white-label" sx={{ py: { xs: 8, md: 12 }, px: 3, bgcolor: '#fff' }}>
          <Container maxWidth="lg">
            <SectionHeading
              eyebrow="White Label"
              title="Ihre Marke im Vordergrund"
              subtitle="NextCab ist vollständig als White-Label-Lösung konzipiert — Ihre Kunden nutzen eine App, die aussieht, als wäre sie für Ihr Unternehmen gebaut worden."
            />

            <SplitLayout>
              <Box>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  Farben, Logo und Slogan werden zentral gepflegt und schlagen auf Portal,
                  Kunden-App und Fahrer-App durch. Auch Impressum, Datenschutz und die
                  zuständigen Aufsichtsbehörden sind pro Mandant hinterlegt.
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  Das reicht bis in die App Stores: Dort erscheint Ihr App-Name mit Ihrem Icon.
                  Ihre Fahrgäste laden Ihre App — sie bekommen nie den Namen eines
                  Software-Anbieters zu sehen.
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  Für Zentralen mit mehreren Unternehmern gibt es Subkonten mit sauberer
                  Mandantentrennung — jeder sieht nur seine eigenen Fahrten, Fahrer und Abrechnungen.
                </Typography>

                <Box sx={{ mt: 4, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' } }}>
                  {[
                    { icon: SecurityIcon, label: 'Sicher und zuverlässig' },
                    { icon: DevicesIcon, label: 'Optimiert für alle Geräte' },
                    { icon: TemplateIcon, label: 'Anpassbare Vorlagen' },
                    { icon: PhoneIcon, label: 'Technischer Support' },
                  ].map(({ icon: Icon, label }) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Icon sx={{ color: 'primary.main' }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  height: 400,
                  borderRadius: 5,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 4,
                  color: '#fff',
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, #3a3a3a 100%)`,
                  boxShadow: `0 30px 70px ${alpha('#000', 0.25)}`,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(circle at 50% 30%, ${alpha(theme.palette.primary.main, 0.28)} 0%, transparent 60%)`,
                  }}
                />
                <Box sx={{ position: 'relative', textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 96,
                      height: 96,
                      mx: 'auto',
                      mb: 3,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="h5" sx={{ color: 'primary.contrastText', fontWeight: 800 }}>
                      LOGO
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Ihr Brand
                  </Typography>
                  <Typography variant="h6" sx={{ color: alpha('#fff', 0.6), fontWeight: 400, mb: 2 }}>
                    Taxi App
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.55) }}>
                    Vollständig anpassbar an Ihre Unternehmensidentität
                  </Typography>
                </Box>
              </Box>
            </SplitLayout>
          </Container>
        </Box>
      )}

      {/* Herkunftsnachweis - bewusst ruhig gehalten, kein eigener Marketingblock */}
      {(!directLogin && isDefaultApp) && (
        <Box sx={{ py: { xs: 6, md: 8 }, px: 3, bgcolor: '#fafafa', borderTop: `1px solid ${alpha('#000', 0.06)}` }}>
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center' }}>
              <Eyebrow>Woher das kommt</Eyebrow>
              <Typography
                variant="h5"
                component="h2"
                sx={{ fontWeight: 700, color: 'secondary.main', mb: 2 }}
              >
                Von Unternehmern für Unternehmer
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, maxWidth: 720, mx: 'auto' }}>
                {brandingConfig.appName} entsteht nicht am Reißbrett. Was Sie hier sehen, ist aus
                dem Tagesgeschäft von Taxi- und Mietwagenunternehmen gewachsen — Funktion für
                Funktion, gemeinsam mit unseren Kunden, oft weil im Alltag etwas gefehlt hat.
                Wir arbeiten täglich weiter daran.
              </Typography>
            </Box>
          </Container>
        </Box>
      )}

      {/* Login Section - unterschiedliche ID für Standard-App und White-Label-App */}
      <Box
        id={isDefaultApp ? "client-login" : "login-section"}
        ref={loginSectionRef}
        sx={{
          py: { xs: 8, md: 12 },
          px: 3,
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 5, md: 8 },
              alignItems: 'center',
              gridTemplateColumns: { xs: '1fr', md: '1fr minmax(360px, 440px)' },
            }}
          >
            <Fade in timeout={900}>
              <Box>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{ color: 'primary.contrastText', fontWeight: 800, letterSpacing: '-0.02em', mb: 2, fontSize: { xs: '2rem', md: '2.75rem' } }}
                >
                  Admin-Portal
                </Typography>
                <Typography variant="h6" component="p" sx={{ color: alpha(theme.palette.primary.contrastText, 0.75), fontWeight: 400, mb: 3 }}>
                  Melden Sie sich an, um auf Ihr {brandingConfig.appName} Administrator-Portal zuzugreifen.
                </Typography>
                <Typography variant="body1" sx={{ color: alpha(theme.palette.primary.contrastText, 0.7), maxWidth: 520, lineHeight: 1.8 }}>
                  Von hier aus verwalten Sie Ihren gesamten Taxibetrieb — Fahrer, Fahrzeuge,
                  Fahrten, Vorbestellungen und Krankenfahrten. Alles in einer übersichtlichen Oberfläche.
                </Typography>
              </Box>
            </Fade>

            <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
              <LoginContainer elevation={0}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', width: '100%', marginBottom: 3 }}>
                    {/* White-Label-Anpassung: Dieser Mandant führt nur ein Logo
                        (primary und icon zeigen auf dieselbe Datei), deshalb kein
                        separates Icon davor und keine Überlappung. */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%',
                    }}>
                      <PrimaryLogo
                        alt={brandingConfig.appName}
                        style={{ height: isMobile ? 120 : 160 }}
                      />
                    </Box>
                    <Typography variant="subtitle1" color="black" sx={{
                      mt: 2,
                      fontWeight: 300,
                      letterSpacing: 1,
                    }}>
                      {brandingConfig.slogan}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="textSecondary" align="left" sx={{ alignSelf: 'flex-start', ml: 1 }}>
                    Login Admin-Portal
                  </Typography>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    <AlertTitle>Fehler</AlertTitle>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="E-Mail-Adresse"
                    name="email"
                    autoComplete="email"
                    autoFocus={directLogin || !isDefaultApp}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!emailError}
                    helperText={emailError}
                    onBlur={() => validateEmail(email)}
                  />
                  <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Passwort"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={!!passwordError}
                    helperText={passwordError}
                    onBlur={() => validatePassword(password)}
                  />

                  <Link
                    component={RouterLink}
                    to="/passwort-vergessen"
                    variant="body2"
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      mt: 1,
                      // Primärgelb auf weißer Karte ist kaum lesbar — hier
                      // bewusst die dunkle Sekundärfarbe.
                      color: 'secondary.main',
                      textDecorationColor: alpha('#000', 0.3),
                    }}
                  >
                    Passwort vergessen?
                  </Link>

                  <SubmitButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="secondary"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Anmelden'
                    )}
                  </SubmitButton>
                </Form>

                {/* Links zu rechtlichen Seiten */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 3 }}>
                  <Link
                    component={RouterLink}
                    to="/datenschutz"
                    variant="body2"
                    color="textSecondary"
                  >
                    Datenschutz
                  </Link>
                  <Link
                    component={RouterLink}
                    to="/impressum"
                    variant="body2"
                    color="textSecondary"
                  >
                    Impressum
                  </Link>
                </Box>
              </LoginContainer>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Partner- und Technologieleiste - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <PartnerStrip
          appStoreUrl={storeUrls.appStoreUrl}
          playStoreUrl={storeUrls.playStoreUrl}
        />
      )}

      {/* Footer - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <Box id="contact" sx={{ bgcolor: 'secondary.main', color: '#fff', py: 8, px: 3 }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'grid',
                gap: 5,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: '1.4fr 1fr 1fr' },
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <IconLogoOnDark
                    alt={`${brandingConfig.appName} Logo`}
                    style={{ height: 44, marginRight: 0 }}
                  />
                  <PrimaryLogoOnDark
                    alt={brandingConfig.appName}
                    style={{ height: 130, marginLeft: -4 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ mb: 2, color: alpha('#fff', 0.65), maxWidth: 340, lineHeight: 1.7 }}>
                  Die komplette Lösung für moderne Taxi-Unternehmen — von der Disposition
                  über Krankenfahrten bis zur KI-Telefonie.
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#fff', 0.45) }}>
                  {brandingConfig.copyright}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Navigation
                </Typography>
                <Box component="nav">
                  {navItems.map((item) => (
                    <FooterLink
                      key={item.text}
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        smoothScrollToElement(item.href.substring(1));
                      }}
                    >
                      {item.text}
                    </FooterLink>
                  ))}
                  <Divider sx={{ my: 1.5, borderColor: alpha('#fff', 0.12) }} />
                  <FooterLink component={RouterLink} to="/datenschutz">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PrivacyTipIcon fontSize="small" sx={{ mr: 1 }} />
                      Datenschutzerklärung
                    </Box>
                  </FooterLink>
                  <FooterLink component={RouterLink} to="/impressum">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <GavelIcon fontSize="small" sx={{ mr: 1 }} />
                      Impressum
                    </Box>
                  </FooterLink>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Kontakt
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: alpha('#fff', 0.65) }}>
                  Haben Sie Fragen oder möchten mehr erfahren?
                </Typography>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<PhoneIcon />}
                  href={`tel:${brandingConfig.phone.replace(/\s/g, '')}`}
                  sx={{ mb: 1.5, borderColor: alpha('#fff', 0.3) }}
                >
                  {brandingConfig.phone}
                </Button>
                <br />
                <Button
                  variant="outlined"
                  color="inherit"
                  href={`mailto:${brandingConfig.email}`}
                  sx={{ borderColor: alpha('#fff', 0.3), textTransform: 'none' }}
                >
                  {brandingConfig.email}
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* Vereinfachter Footer für White-Label Apps */}
      {(!directLogin && !isDefaultApp) && (
        <Box sx={{ bgcolor: 'secondary.main', color: '#fff', py: 4, px: 3 }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                alignItems: 'center',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <IconLogoOnDark
                    alt={`${brandingConfig.appName} Logo`}
                    style={{ height: 36, marginRight: 0 }}
                  />
                  <PrimaryLogoOnDark
                    alt={brandingConfig.appName}
                    style={{ height: 100, marginLeft: -4 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: alpha('#fff', 0.6) }}>
                  {brandingConfig.copyright}
                </Typography>
              </Box>
              <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Typography variant="body2" sx={{ mb: 1, color: alpha('#fff', 0.75) }}>
                  {brandingConfig.phone} | {brandingConfig.email}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: alpha('#fff', 0.6) }}>
                  {brandingConfig.address}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <FooterLink component={RouterLink} to="/datenschutz" sx={{ mb: 0 }}>
                    Datenschutzerklärung
                  </FooterLink>
                  <FooterLink component={RouterLink} to="/impressum" sx={{ mb: 0 }}>
                    Impressum
                  </FooterLink>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      )}
    </>
  );
};

export default LandingPage;
